import { fabric } from 'fabric';
import { type Rect } from '../types';
import type WhiteboardController from '../controllers/WhiteboardController';
import { combinedPosition } from './geometry';
import type Canvas from '../objects/Canvas';
import type SimpleSelection from 'fabric/SimpleSelection';
import { type IPoint } from 'fabric/fabric-impl';

export const DEFAULT_MIN_ZOOM = 0.05;
const MAX_ZOOM = 4.0;
const ZOOM_MARGIN_FACTOR = 0.8;
const DEFAULT_ANIMATION_TIME = 250;
const VPT_PREFIX = 'vpt-';

// Dynamically compute the minimum zoom based on size of the active canvas.
function computeMinZoom(canvas: Canvas) {
  const usedRect = canvas.usedPortion();
  const zoomX = canvas.getWidth() / usedRect.width;
  const zoomY = canvas.getHeight() / usedRect.height;
  let mZoom = Math.min(zoomX, zoomY) * ZOOM_MARGIN_FACTOR;
  if (mZoom > DEFAULT_MIN_ZOOM) mZoom = DEFAULT_MIN_ZOOM;

  return mZoom;
}

// Gives zoom (out) factor that will contain the object
function getZoomToContain(
  object: fabric.Object,
  currentZoom: number,
  viewport
): number {
  const bounds = object.getBoundingRect(true, true);
  const right = bounds.left + bounds.width;
  const bottom = bounds.top + bounds.height;

  // Compute ratio to zoom out by according to which edge is going out of bounds
  const factor = Math.min(
    1,
    bounds.left < viewport.tl.x ? viewport.tl.x / bounds.left : 1,
    bounds.top < viewport.tl.y ? viewport.tl.y / bounds.top : 1,
    right > viewport.tr.x ? viewport.tr.x / right : 1,
    bottom > viewport.br.y ? viewport.br.y / bottom : 1
  );
  return currentZoom * factor * ZOOM_MARGIN_FACTOR;
}

// Set the canvas zoom level around a point, with animation.
function setZoom(
  controller: WhiteboardController,
  zoom: number,
  centerPoint: { x: number; y: number } | null = null,
  animate = true
): void {
  const canvas = controller.canvas;
  const oldZoom = canvas.getZoom();
  const minZoom = computeMinZoom(canvas);

  if (zoom > MAX_ZOOM) zoom = MAX_ZOOM;
  if (zoom < minZoom) zoom = minZoom;

  if (!centerPoint) {
    // Zoom relative to the center of the viewport.
    const vpt = canvas.vptCoords!;
    centerPoint = {
      x: (oldZoom * (vpt.tr.x - vpt.tl.x)) / 2,
      y: (oldZoom * (vpt.bl.y - vpt.tl.y)) / 2,
    };
  }

  const doTransform = (p: number) => {
    canvas.zoomToPoint(centerPoint, oldZoom + p * (zoom - oldZoom));
  };

  if (animate) {
    animateViewportTransform(controller, doTransform, { duration: 200 });
  } else {
    doTransform(1);
    canvas.fire('zoom');
  }
}

function interpolate(start: number, end: number, percent: number): number {
  return start + (end - start) * percent;
}

function interpolateArray(
  start: number[],
  end: number[],
  percent: number
): number[] {
  return start.map((v, i) => interpolate(v, end[i], percent));
}

function animateViewportTransform(
  controller: WhiteboardController,
  target: number[] | ((progress: number) => void),
  options?: { duration?: number; onComplete?: () => void }
) {
  const canvas = controller.canvas;
  const oldViewportTransform = canvas.viewportTransform;
  if (!oldViewportTransform) return;

  const onChange = (p: number) => {
    if (Array.isArray(target)) {
      canvas.setViewportTransform(
        p < 1 ? interpolateArray(oldViewportTransform, target, p) : target
      );
    } else {
      target(p);
    }
    canvas.fire('zoom');
  };

  const onComplete = () => {
    onChange(1);
    canvas.fire('zoom');
    controller.setAnimationCancelFunction(null);
    controller.actionShowObjectToolbar();
    options?.onComplete?.();
  };

  controller.actionHideObjectToolbar();

  const cancelFunc = fabric.util.animate({
    startValue: 0,
    endValue: 1,
    duration: options?.duration ?? DEFAULT_ANIMATION_TIME,
    onChange,
    onComplete,
  });

  controller.setAnimationCancelFunction(() => {
    // @ts-expect-error fabric.util.animate is incorrectly typed
    cancelFunc();
    onComplete();
  });
}

type RectOrObject = Rect | fabric.Object | (fabric.Object | Rect)[];

const normaliseRect = (obj: fabric.Object | Rect): Rect =>
  'absolutePosition' in obj ? obj.absolutePosition() : obj;

const getCombinedRect = (target: RectOrObject): Rect =>
  Array.isArray(target)
    ? combinedPosition(target.map(normaliseRect))
    : normaliseRect(target);

const isContainedWithinRect = (
  pointTL: IPoint,
  pointBR: IPoint,
  object: RectOrObject
) => {
  const boundingRect = getCombinedRect(object);

  return (
    boundingRect.left >= pointTL.x &&
    boundingRect.left + boundingRect.width <= pointBR.x &&
    boundingRect.top >= pointTL.y &&
    boundingRect.top + boundingRect.height <= pointBR.y
  );
};

interface Area {
  width: number;
  height: number;
  // offsets in case there are elements on the left or top that should not be covered
  x?: number;
  y?: number;
}

/** Determines the available canvas area to be used when panning to an object, taking visible
 * sidebars etc into account. */
const getAvailableArea = (controller: WhiteboardController): Area => {
  const toolbarOffset =
    !controller.isReadOnly() && controller.props.allowUpdate ? 55 : 0;
  const baselineToolbarOffset = controller.props.isEmbeddedPreview ? 0 : 55;

  return {
    width: controller.canvas.getWidth() - toolbarOffset,
    height: controller.canvas.getHeight() - baselineToolbarOffset,
    x: toolbarOffset,
  };
};

interface ZoomToOptions {
  marginFactor?: number;
  maxZoom?: number;
  animate?: boolean;
  zoomInOnly?: boolean;
  zoomOutOnly?: boolean;
  /** "center" centers the target in the viewport. "minimal" only pans/zooms enough to make the target visible */
  behavior?: 'center' | 'minimal';
  onComplete?: () => void;
}

// Zoom and pan to bring a rect into view.
async function zoomAndPanTo(
  controller: WhiteboardController,
  rectOrObject: RectOrObject,
  {
    marginFactor = ZOOM_MARGIN_FACTOR,
    maxZoom = MAX_ZOOM,
    animate = true,
    zoomInOnly,
    zoomOutOnly,
    behavior = 'center',
    ...opts
  }: ZoomToOptions = {}
) {
  const target = getCombinedRect(rectOrObject);
  const canvas = controller.canvas;

  if (zoomInOnly) {
    // determine w/h of target, and only zoom if it is smaller than the viewport,
    // to avoid zooming out
    const zoom = canvas.getZoom();
    const available = {
      width: (marginFactor * canvas.getWidth()) / zoom,
      height: (marginFactor * canvas.getHeight()) / zoom,
    };

    if (available.width <= target.width || available.height <= target.height) {
      return;
    }
  }

  const maxZoomValue = zoomOutOnly
    ? canvas.getZoom()
    : zoomInOnly
    ? Math.max(maxZoom, canvas.getZoom())
    : maxZoom;

  const newVpt = determineZoomVpt(getAvailableArea(controller), target, {
    minZoom: computeMinZoom(canvas),
    maxZoom: maxZoomValue,
    marginFactor,
    currentVpt:
      behavior === 'minimal'
        ? (canvas.viewportTransform as TransformMatrix)
        : undefined,
  });

  return await new Promise<void>(resolve => {
    const onComplete = () => {
      opts.onComplete?.();
      resolve();
    };
    if (animate) {
      animateViewportTransform(controller, newVpt, {
        onComplete,
      });
    } else {
      canvas.setViewportTransform(newVpt);
      canvas.fire('zoom');
      onComplete();
    }
  });
}
/** Attempts to zoom out to fit the object. Returns false if it cannot zoom out
 * any further so calling context can take a different action (e.g., panning)
 */
function zoomOutToFit(
  controller: WhiteboardController,
  object: SimpleSelection | fabric.Object
): boolean {
  const viewport = controller.canvas.calcViewportBoundaries();
  if (!object.isContainedWithinRect(viewport.tl, viewport.br, true)) {
    const currentZoom = controller.canvas.getZoom();
    const newZoom = getZoomToContain(object, currentZoom, viewport);
    if (newZoom > DEFAULT_MIN_ZOOM && newZoom < currentZoom) {
      setZoom(controller, newZoom, null, false);
    } else {
      return false;
    }
  }

  return true;
}

function objectIsInView(
  controller: WhiteboardController,
  object: RectOrObject
): boolean {
  const viewport = controller.canvas.calcViewportBoundaries();
  const objects = Array.isArray(object) ? object : [object];

  return !objects.some(
    obj => !isContainedWithinRect(viewport.tl, viewport.br, obj)
  );
}

/** Performs minimal pan/zoom to bring an object into view while keeping the existing
 * visible objects within the viewport. */
async function bringObjectIntoView(
  controller: WhiteboardController,
  object: RectOrObject,
  opts: Omit<ZoomToOptions, 'zoomOutOnly'> = {}
) {
  const viewport = controller.canvas.calcViewportBoundaries();
  const objects = Array.isArray(object) ? object : [object];
  if (objectIsInView(controller, objects)) {
    return;
  }

  const currentVisible = controller.canvas
    .getObjects()
    .filter(o => o.isContainedWithinRect(viewport.tl, viewport.br, true));

  return await zoomAndPanTo(controller, [...currentVisible, ...objects], {
    ...opts,
    zoomOutOnly: true,
    behavior: 'minimal',
  });
}

/** Pans the viewport just enought to contain object without affecting zoom. This
 * acts as a wrapper around zoomAndPanTo with a simplified interface. Could be
 * improved to avoid centering selection, which can be a drastic jump depending
 * on context
 */
function panTo(
  controller: WhiteboardController,
  rectOrObject: RectOrObject,
  { animate = true }: Pick<ZoomToOptions, 'animate'> = {}
) {
  zoomAndPanTo(controller, rectOrObject, {
    maxZoom: controller.canvas.getZoom(),
    animate,
  });
}

/** horizontal zoom, vertical skew, horizontal skew, vertical zoom, horizontal pan, vertical pan */
type TransformMatrix = [number, number, number, number, number, number];

function determineZoomVpt(
  area: Area,
  rectOrObject: RectOrObject,
  options?: {
    maxZoom?: number;
    minZoom?: number;
    marginFactor?: number;
    /** if provided, determines minimal viewport change to bring target into view */
    currentVpt?: TransformMatrix;
  }
): TransformMatrix {
  const rect = getCombinedRect(rectOrObject);

  const zoomX = area.width / rect.width;
  const zoomY = area.height / rect.height;
  const maxZoom = options?.maxZoom ?? MAX_ZOOM;
  const marginFactor = options?.marginFactor ?? ZOOM_MARGIN_FACTOR;

  let newZoom = Math.min(zoomX, zoomY) * marginFactor;
  if (newZoom > maxZoom) newZoom = maxZoom;
  if (options?.minZoom && newZoom < options.minZoom) newZoom = options.minZoom;

  let panX =
    (area.width / newZoom / 2 - rect.left - rect.width / 2) * newZoom +
    (area.x ?? 0);
  let panY =
    (area.height / newZoom / 2 - rect.top - rect.height / 2) * newZoom +
    (area.y ?? 0);

  if (options?.currentVpt) {
    const [, , , , currentX, currentY] = options.currentVpt;

    // determine if there's extra space around the target rect
    const hSpace = (area.width * marginFactor - rect.width * newZoom) / 2;
    const vSpace = (area.height * marginFactor - rect.height * newZoom) / 2;

    // if there's space, reduce the pan amount so it's just enough to bring the target into view
    if (vSpace > 0) {
      panY =
        currentY < panY
          ? Math.max(currentY, panY - vSpace)
          : Math.min(currentY, panY + vSpace);
    }
    if (hSpace > 0) {
      panX =
        currentX < panX
          ? Math.max(currentX, panX - hSpace)
          : Math.min(currentX, panX + hSpace);
    }
  }

  return [newZoom, 0, 0, newZoom, panX, panY];
}

/** Get the target viewport based on the location hash, falling
 * back to what's in localstorage if unsuccessful */
const getTargetViewport = (
  controller: WhiteboardController,
  hash: string,
  getStoredHash?: () => string | undefined
): TransformMatrix | undefined => {
  // first check if it's direct object link
  if (hash.startsWith('object-')) {
    const objectIds = hash.split('-').slice(1);
    const objects = controller.canvas
      .getObjects()
      .filter(o => objectIds.includes(o.id));

    if (objects.length) {
      return determineZoomVpt(getAvailableArea(controller), objects);
    }
  }

  // then check if either the url or the localstorage value are a viewport hash
  return parseViewportHash(hash) ?? parseViewportHash(getStoredHash?.());
};

/** Attempts to parse a base64-encoded value like PrT9tUMmpABD8fV0 and convert it to a viewport array */
const parseViewportHash = (
  hash: string | undefined
): TransformMatrix | undefined => {
  if (!hash?.startsWith(VPT_PREFIX)) return;

  try {
    const buffer = new Uint8Array(
      atob(hash.slice(VPT_PREFIX.length))
        .split('')
        .map(c => c.charCodeAt(0))
    );

    if (buffer.length) {
      // Now we need to convert the Uint8Array back to a normal array of floats
      const view = new DataView(buffer.buffer);
      return [
        view.getFloat32(0),
        0,
        0,
        view.getFloat32(0),
        view.getFloat32(4),
        view.getFloat32(8),
      ];
    }
  } catch (e) {
    if (e.name !== 'InvalidCharacterError') {
      console.error(e);
    }
  }
};

/** Converts a viewport array into a base64-encoded string suitable for storing in a url */
const createViewportHash = (vpt: TransformMatrix): string => {
  const sliced = vpt.slice(-3);
  const buffer = new ArrayBuffer(sliced.length * 4);
  const view = new DataView(buffer);
  sliced.forEach((value, index) => view.setFloat32(index * 4, value));
  return (
    VPT_PREFIX + btoa(String.fromCharCode.apply(null, new Uint8Array(buffer)))
  );
};

export {
  setZoom,
  zoomOutToFit,
  zoomAndPanTo,
  panTo,
  animateViewportTransform,
  MAX_ZOOM,
  determineZoomVpt,
  getTargetViewport,
  createViewportHash,
  objectIsInView,
  bringObjectIntoView,
  type TransformMatrix,
  type ZoomToOptions,
};
